import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ScrollArea, ScrollBar } from './ui/scroll-area';
import { MoreHorizontal, ExternalLink, Trash2, Plus, ArrowUpDown, ArrowUp, ArrowDown, Edit2, Check, X, Info } from 'lucide-react';
import { parseIgUsername, rowTakesUsername } from '../../../../../../modules/brand/components/campaigns/postLinkRules';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import { BASE_URL_UI, DummyCampaignImage } from '../../../../../../config/config';
import { Link } from 'react-router-dom';
import { CONTENT_TYPE_COLORS, getTierBadgeStyle } from './ui/utils';
import { AnalyticsService } from '../../../../../../core/services';
import { NoData } from './ui/noData';
import { DarkTooltip } from './ui/darkTooltip';
import { usePdfMode, usePdfVariant, pdfText } from './pdf/PdfModeContext';
import { FormValidation, Methods } from '../../../../../../core/utility';

import NumberWithTooltip from './ui/NumberWithTooltip';
import { Tooltip } from '@mui/material';
import { Skeleton } from '../../../../../../components/tailwind-component/ui/skeleton';
import MaterialSymbol from '../../../../../../components/icons/material-symbol';
import ManualViewsGrid from '../../../components/manual-views-grid';
import { BulkAddPosts } from './BulkAddPosts';

const analyticsService = new AnalyticsService();
const formValidation = new FormValidation();
const methods = new Methods();

interface ErrorTypes {
  status: boolean;
  message: string;
}
interface PostsTableProps {
  platform: string;
  isAddPostDialogOpen?: boolean;
  onAddPostDialogChange?: (open: boolean) => void;
  posts?: any | [];
  onPostsChange?: (posts: any | []) => void;
  onDeleteSuccess?: () => void;
  linkedCampaignId: number;
  page:number
  setPage: React.Dispatch<React.SetStateAction<number>>;
  limit:number;
  loading:boolean;
  totalCount:number;
  setLimit:React.Dispatch<React.SetStateAction<number>>;
  shared?:boolean;
  // Campaign has >=1 Facebook post in range (campaign-wide, not just this page)
  hasFacebookPosts?: boolean;
  // Campaign has >=1 Instagram post in range -- reposts are only stored for Instagram
  hasInstagramPosts?: boolean;
}

const colors = CONTENT_TYPE_COLORS;

// user_role_id values allowed to enter post views by hand: super_admin (1),
// admin (2), bd_admin (3). Mirrors Analytics.MANUAL_VIEWS_ROLE_IDS on the API.
const MANUAL_VIEWS_ROLE_IDS = [1, 2, 3];

// export interface Post {
//   id: number;
//   username: string;
//   title: string;
//   platform: string;
//   type: string;
//   publishedDate: string;
//   views: number;
//   likes: number;
//   comments: number;
//   shares: number;
//   er: number;
//   cpe: number;
//   cpl: number;
//   cpc: number;
//   cpv: number;
//   cost?: number;
//   postUrl?: string;
// }

const initialPosts = [
  {
    id: 1,
    username: '@sarahjohnson',
    title: 'Summer Skincare Routine 2024',
    platform: 'Instagram',
    type: 'Reel',
    publishedDate: '25-08-05',
    views: 124500,
    likes: 12450,
    comments: 892,
    shares: 345,
    er: 10.0,
    cpe: 0.82,
    cpl: 0.12,
    cpc: 1.45,
    cpv: 0.012,
    cost: 1500,
    postUrl: 'https://instagram.com/p/example1'
  },
  {
    id: 2,
    username: '@emmawilliams',
    title: 'Unboxing New Beauty Products',
    platform: 'YouTube',
    type: 'Video',
    publishedDate: '25-08-03',
    views: 98700,
    likes: 8876,
    comments: 543,
    shares: 0,
    er: 9.0,
    cpe: 0,
    cpl: 0,
    cpc: 0,
    cpv: 0,
    postUrl: 'https://youtube.com/watch?v=example2'
  },
  {
    id: 3,
    username: '@lisachen',
    title: 'Morning Skincare Tips',
    platform: 'Instagram',
    type: 'Photo',
    publishedDate: '25-08-01',
    views: 87600,
    likes: 7446,
    comments: 456,
    shares: 234,
    er: 8.5,
    cpe: 0.78,
    cpl: 0.11,
    cpc: 1.32,
    cpv: 0.009,
    cost: 800,
    postUrl: 'https://instagram.com/p/example3'
  },
  {
    id: 4,
    username: '@michaelbrown',
    title: 'Product Review: Natural Moisturizer',
    platform: 'Facebook',
    type: 'Video',
    publishedDate: '25-07-30',
    views: 76500,
    likes: 6120,
    comments: 389,
    shares: 456,
    er: 8.0,
    cpe: 0,
    cpl: 0,
    cpc: 0,
    cpv: 0,
    postUrl: 'https://facebook.com/example4'
  },
  {
    id: 5,
    username: '@jessicamartinez',
    title: 'Makeup Tutorial - Natural Look',
    platform: 'Instagram',
    type: 'Reel',
    publishedDate: '25-07-28',
    views: 69800,
    likes: 5235,
    comments: 312,
    shares: 189,
    er: 7.5,
    cpe: 0.92,
    cpl: 0.13,
    cpc: 1.56,
    cpv: 0.014,
    cost: 950,
    postUrl: 'https://instagram.com/p/example5'
  },
  {
    id: 6,
    username: '@davidlee',
    title: 'Skincare Myths Debunked',
    platform: 'YouTube',
    type: 'Short',
    publishedDate: '25-07-25',
    views: 65400,
    likes: 4908,
    comments: 278,
    shares: 0,
    er: 7.2,
    cpe: 0,
    cpl: 0,
    cpc: 0,
    cpv: 0,
    postUrl: 'https://youtube.com/shorts/example6'
  },
  {
    id: 7,
    username: '@amyjones',
    title: 'Fall Beauty Haul',
    platform: 'Facebook',
    type: 'Image',
    publishedDate: '25-07-22',
    views: 54300,
    likes: 3876,
    comments: 234,
    shares: 312,
    er: 6.8,
    cpe: 0.91,
    cpl: 0.13,
    cpc: 1.42,
    cpv: 0.011,
    cost: 600,
    postUrl: 'https://facebook.com/example7'
  },
  {
    id: 8,
    username: '@robertgarcia',
    title: 'Best Night Creams for Dry Skin',
    platform: 'YouTube',
    type: 'Video',
    publishedDate: '25-07-20',
    views: 112300,
    likes: 9876,
    comments: 654,
    shares: 0,
    er: 8.9,
    cpe: 0.87,
    cpl: 0.14,
    cpc: 1.98,
    cpv: 0.019,
    cost: 2100,
    postUrl: 'https://youtube.com/watch?v=example8'
  },
  {
    id: 9,
    username: '@sophiaturner',
    title: 'My Daily Wellness Routine',
    platform: 'Instagram',
    type: 'Reel',
    publishedDate: '25-07-18',
    views: 93200,
    likes: 8124,
    comments: 567,
    shares: 276,
    er: 9.2,
    cpe: 0.76,
    cpl: 0.10,
    cpc: 1.23,
    cpv: 0.011,
    cost: 1050,
    postUrl: 'https://instagram.com/p/example9'
  },
  {
    id: 10,
    username: '@danielkim',
    title: 'Affordable Skincare Alternatives',
    platform: 'Facebook',
    type: 'Video',
    publishedDate: '25-07-15',
    views: 67800,
    likes: 5234,
    comments: 423,
    shares: 389,
    er: 8.3,
    cpe: 0,
    cpl: 0,
    cpc: 0,
    cpv: 0,
    postUrl: 'https://facebook.com/example10'
  },
  {
    id: 11,
    username: '@oliviawhite',
    title: 'Glow Up Transformation',
    platform: 'Instagram',
    type: 'Photo',
    publishedDate: '25-07-12',
    views: 78900,
    likes: 6345,
    comments: 389,
    shares: 198,
    er: 8.0,
    cpe: 0,
    cpl: 0,
    cpc: 0,
    cpv: 0,
    postUrl: 'https://instagram.com/p/example11'
  },
  {
    id: 12,
    username: '@ethanthomas',
    title: 'Hair Care Tips for Men',
    platform: 'YouTube',
    type: 'Short',
    publishedDate: '25-07-10',
    views: 56700,
    likes: 4321,
    comments: 234,
    shares: 0,
    er: 7.6,
    cpe: 0,
    cpl: 0,
    cpc: 0,
    cpv: 0,
    postUrl: 'https://youtube.com/shorts/example12'
  },
  {
    id: 13,
    username: '@miaroberts',
    title: 'Spring Makeup Trends',
    platform: 'Facebook',
    type: 'Image',
    publishedDate: '25-07-08',
    views: 48900,
    likes: 3654,
    comments: 198,
    shares: 267,
    er: 7.1,
    cpe: 0.89,
    cpl: 0.13,
    cpc: 1.38,
    cpv: 0.010,
    cost: 500,
    postUrl: 'https://facebook.com/example13'
  },
  {
    id: 14,
    username: '@noahwalker',
    title: 'Top 10 Serums for Anti-Aging',
    platform: 'YouTube',
    type: 'Video',
    publishedDate: '25-07-05',
    views: 134500,
    likes: 11234,
    comments: 876,
    shares: 0,
    er: 9.5,
    cpe: 0.81,
    cpl: 0.13,
    cpc: 1.87,
    cpv: 0.017,
    cost: 2300,
    postUrl: 'https://youtube.com/watch?v=example14'
  },
  {
    id: 15,
    username: '@avamartin',
    title: 'Weekend Skincare Routine',
    platform: 'Instagram',
    type: 'Reel',
    publishedDate: '25-07-03',
    views: 102300,
    likes: 9087,
    comments: 678,
    shares: 345,
    er: 9.8,
    cpe: 0.73,
    cpl: 0.09,
    cpc: 1.12,
    cpv: 0.008,
    cost: 850,
    postUrl: 'https://instagram.com/p/example15'
  }
];

type SortColumn = 'views' | 'likes' | 'comments' | 'er' | 'cost' | 'cpv' | 'cpl' | 'cpc' | 'cpe';
type SortDirection = 'asc' | 'desc' | null;

export function PostsTable({ totalCount, loading,page,
  setPage,
  setLimit,
  limit,platform, isAddPostDialogOpen = false, onAddPostDialogChange, posts: propPosts, onPostsChange, onDeleteSuccess, linkedCampaignId, shared, hasFacebookPosts = false, hasInstagramPosts = false }: PostsTableProps) {
  const [internalPosts, setInternalPosts] = useState<any | []>(initialPosts);
  const [postLink, setPostLink] = useState('');
  const [cost, setCost] = useState('');
  // optional: username of whoever posted it (stored for Instagram links; admins only)
  const [newUsername, setNewUsername] = useState('');
  const [newUsernameError, setNewUsernameError] = useState<string | undefined>(undefined);
  const [deletePostId, setDeletePostId] = useState<number | null>(null);
  const [internalDialogOpen, setInternalDialogOpen] = useState(false);
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [editingCostId, setEditingCostId] = useState<number | null>(null);
  const [editCostValue, setEditCostValue] = useState('');
  const [selectedPost, setSelectedPost] = useState<any>(null);
  const [errorPostLink, setErrorPostLink] = useState<ErrorTypes>();
  // "Add username" modal (Instagram rows): the post being edited + the typed username
  const [usernamePost, setUsernamePost] = useState<any>(null);
  const [usernameValue, setUsernameValue] = useState('');
  const [usernameError, setUsernameError] = useState<string | undefined>(undefined);
  const [savingUsername, setSavingUsername] = useState(false);
  // url_id -> username already saved in campaign_analytics (from the API, plus ones saved this session)
  const [savedUsernames, setSavedUsernames] = useState<Record<number, string>>({});
  const [checkingUsernames, setCheckingUsernames] = useState(false);

  // ---- Manual views entry (admin only) ------------------------------------
  // Bright Data frequently returns 0 plays for Instagram posts, which leaves
  // the Views column and the Views-over-time chart flat at 0. super_admin /
  // admin / bd_admin can fill the real number in per date. The backend
  // re-verifies the role and refuses to overwrite a non-zero scraped value,
  // so this gate is purely about what we render.
  const currentUser = useSelector((state: any) => state?.UserReducer?.UserData);
  const canEditViews =
    !shared && MANUAL_VIEWS_ROLE_IDS.includes(Number(currentUser?.user_role_id));
  // Username tools (Add Post field, "Add username"): users whose role has the Brand Pulse
  // permission -- the same flags the header uses to show Brand Pulse; the API re-checks them
  // from the login token (brand-pulse-access.ts).
  const canAddUsernames = !shared &&
    (currentUser?.view_admin_dashboard === 'True' || currentUser?.brand_pulse === 'True');

  // Replaced the per-post dialog with the shared posts x dates grid -- filling
  // these in one at a time was too slow. Same component and endpoints Campaign
  // Analytics uses.
  const [viewsGridOpen, setViewsGridOpen] = useState(false);

  // Use controlled or uncontrolled dialog state
  const dialogOpen = onAddPostDialogChange ? isAddPostDialogOpen : internalDialogOpen;
  const setDialogOpen = onAddPostDialogChange || setInternalDialogOpen;

  // Use controlled or uncontrolled posts
  const posts = propPosts || internalPosts;

  const setPosts = onPostsChange || setInternalPosts;
  // Handle sorting
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      // Toggle direction: asc -> desc -> null
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection(null);
        setSortColumn(null);
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };
  // Get sorted posts
  const sortedPosts = [...posts].sort((a, b) => {
    if (!sortColumn || !sortDirection) return 0;

    let aValue: any = a[sortColumn];
    let bValue: any = b[sortColumn];

    // Handle undefined/null cost values - treat as 0 or place at end
    if (sortColumn === 'cost') {
      aValue = aValue ?? 0;
      bValue = bValue ?? 0;
    }

    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const displayPosts = sortColumn && sortDirection ? sortedPosts : posts;
  // PDF report: every post, captions printed (shortened), no pagination
  const pdfMode = usePdfMode();
  const pdfVariant = usePdfVariant();
  // Instagram couldn't be read for this post (scraper stored no data): deleted,
  // made private, or a broken link -- shown instead of a blank "@" row
  const isUnavailable = (post: any) => !post?.title && !post?.username;
  const unavailableReason = (post: any) =>
    post?.warning_code === 'dead_page'
      ? 'Link broken, or the post/profile was removed or made private'
      : post?.warning_code
      ? 'Instagram data could not be fetched for this link'
      : 'Data not fetched yet, or the post is no longer available';

  // Render sort icon
  const renderSortIcon = (column: SortColumn) => {
    if (pdfMode) return null; // not clickable in the PDF; keeps the columns narrow enough to fit the page
    if (sortColumn !== column) {
      return <ArrowUpDown className="ml-1 h-4 w-4 inline" />;
    }
    if (sortDirection === 'asc') {
      return <ArrowUp className="ml-1 h-4 w-4 inline" />;
    }
    return <ArrowDown className="ml-1 h-4 w-4 inline" />;
  };

  const getPlatformColor = (platform: string) => {
    switch (platform) {
      case 'Instagram':
        return 'bg-pink-100 text-pink-700';
      case 'Facebook':
        return 'bg-blue-100 text-blue-700';
      case 'YouTube':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  // Show platform column only for "all" platforms view
  const showPlatformColumn = platform === 'all';

  // Show shares column only for Facebook platform
  // const showSharesColumn = platform === 'all' || platform?.toLowerCase() === 'facebook';
  // Shares is only stored for Facebook -- on All Platforms show it only when the campaign has a Facebook post
  const showSharesColumn =
    platform?.toLowerCase() === 'facebook' ||
    (platform === 'all' &&
      (hasFacebookPosts || (propPosts || []).some((p: any) => p?.platform?.toLowerCase() === 'facebook')));
  // Reposts is only stored for Instagram -- same rule as Shares
  const showRepostsColumn =
    platform?.toLowerCase() === 'instagram' ||
    (platform === 'all' &&
      (hasInstagramPosts || (propPosts || []).some((p: any) => p?.platform?.toLowerCase() === 'instagram')));

  const handleAddPost = async () => {
    if (!postLink.trim()) {
      toast.error('Please enter a post link');
      return;
    }
    let campaign_id
    const costValue = cost ? parseFloat(cost) : undefined;
    
    let is_valid_link = false;
     
    // TODO : not getting campaign_id from props
    const validationResponse = formValidation.validatePlatformUrl(platform, postLink)
    setErrorPostLink(validationResponse);
    is_valid_link = validationResponse.status
    platform = validationResponse.platform
    // Canonical link from the validator (e.g. instagram.com/<user>/reel/<code> -> instagram.com/reel/<code>/)
    const finalPostLink = validationResponse.url || postLink.trim();
    // Show the user the link we'll actually save.
    if (finalPostLink !== postLink) setPostLink(finalPostLink);
    // if(platform === 'Instagram'){
    //   campaign_id = '826'
    // }else if(platform === 'Facebook'){
    //   campaign_id = '827'
    // }else{
    //   campaign_id = '999'
    // }
    if (linkedCampaignId && linkedCampaignId > 0) {
      campaign_id = linkedCampaignId;
    } else {
      toast.error('Campaign ID is missing.');
      return;
    }
    const res = await analyticsService.getAnalyticsLinks(campaign_id)
    // Check if postLink already exists in the response data
    // is_duplicate_link = (res.data.rows).some((item:any) => item.post_url === postLink);
    // OLD: const isIncluded = (res.data.rows).some((item:any) => (item.post_url.includes(postLink) || postLink.includes(item.post_url)));
    // Rows with an empty post_url must be ignored - `anyLink.includes('')` is always true, which
    // made every link look "already added" in campaigns that have such rows.
    const isIncluded = (res.data.rows).some((item:any) => {
      const existing = (item?.post_url || '').trim();
      return !!existing && (existing.includes(finalPostLink) || finalPostLink.includes(existing));
    });
    
    if (isIncluded) {
      setErrorPostLink({ status: false, message: 'This link has already been added.' });
      return; 
    }

    
    // optional username: Instagram links only, and it must be a real handle
    const parsedUsername = canAddUsernames && platform === 'Instagram' ? parseIgUsername(newUsername) : { username: '' };
    if (parsedUsername.error) {
      setNewUsernameError(parsedUsername.error);
      return;
    }

    // Create a mock post from the link
    if(is_valid_link){
      const newPost = {
      campaign_id:campaign_id,
      cost: costValue || 0,
      // OLD: post_url: postLink,
      post_url: finalPostLink,
      platform:platform,
      username: parsedUsername.username
    };

    const response = await analyticsService.analyticsAddPost(newPost);
    // OLD: if(response.status){ -- every error status is non-zero too, so failures showed as added
    if(response?.status === 1){
      setPosts([newPost, ...posts]);
      setPostLink('');
      setCost('');
      setNewUsername('');
      setNewUsernameError(undefined);
      setDialogOpen(false);
      toast.success('Post added successfully');
    }else{
      // OLD: toast.success('Post added Failed');
      toast.error(response?.info && !/DB Query|catch/i.test(response.info) ? response.info : 'Could not add the post. Please try again.');
    }

    
    }
    
  };

  const handleDeletePost = async (id: number) => {
    try{
      const response = await analyticsService.analyticsDeletePost({url_id:id});
      if(response.status){
        setPosts(posts.filter((post:any) => (post.url_id ?? post.id) !== id));
        setDeletePostId(null);
        toast.success('Post deleted successfully');
        onDeleteSuccess?.();
      }else{
        toast.error('Post not deleted');
      }
    }catch(error){
      console.error(`handleDeletePost ERROR ::: ${error}`);
    }
    
  };

  // Instagram posts the scraper gave no username for: ask the backend which of them already have one saved
  useEffect(() => {
    if (!canAddUsernames) return;
    const ids = (Array.isArray(posts) ? posts : [])
      .filter((p: any) => !p?.username && (p?.platform || platform)?.toLowerCase() === 'instagram' && p?.url_id)
      .map((p: any) => Number(p.url_id));
    if (!ids.length) return;
    let cancelled = false;
    setCheckingUsernames(true);
    analyticsService.analyticsPostsWithUsername({ url_ids: ids })
      .then((res: any) => {
        if (cancelled || res?.status !== 1) return;
        const found: Record<number, string> = res?.data?.usernames || {};
        setSavedUsernames((prev) => ({ ...prev, ...found }));
      })
      .catch((err: any) => console.error('Username check failed:', err))
      .finally(() => { if (!cancelled) setCheckingUsernames(false); });
    return () => { cancelled = true; };
  }, [posts, platform, canAddUsernames]);

  const handleSaveUsername = async () => {
    if (!usernamePost || savingUsername) return;
    const { username, error } = parseIgUsername(usernameValue);
    if (error || !username) {
      setUsernameError(error || "Enter the creator's Instagram username.");
      return;
    }
    setSavingUsername(true);
    try {
      const response = await analyticsService.analyticsAddPostUsername({ url_id: usernamePost.url_id, username });
      if (response?.status === 1) {
        toast.success(`Username saved. We'll try fetching this post again using @${username}. This usually takes a few minutes.`);
        setSavedUsernames((prev) => ({ ...prev, [Number(usernamePost.url_id)]: username }));
        setUsernamePost(null);
        setUsernameValue('');
        setUsernameError(undefined);
      } else {
        setUsernameError(response?.info || 'Could not save the username. Please try again.');
      }
    } catch (error) {
      console.error(`handleSaveUsername ERROR ::: ${error}`);
      setUsernameError('Could not save the username. Please try again.');
    } finally {
      setSavingUsername(false);
    }
  };

  const handleEditCost = (postId: number, currentCost?: number) => {
    setEditingCostId(postId);
    setEditCostValue(currentCost ? currentCost.toString() : '');
  };

  const handleSaveCost = async (postId: number) => {
    try{
      const costValue = parseFloat(editCostValue);
    if (isNaN(costValue) || costValue < 0) {
      toast.error('Please enter a valid cost');
      return;
    }
    const response = await analyticsService.analyticsAddCost({url_id:postId, cost:costValue});
    if(response.status){
      const updatedPosts = posts.map((post:any) => {
      if (post.url_id === postId) {
        // Calculate CPV, CPL, CPC, CPE based on the new cost
        const totalEngagement = post.likes + post.comments + post.shares;
        return {
          ...post,
          cost: costValue,
          cpv: post.views > 0 ? costValue / post.views : 0,
          cpl: post.likes > 0 ? costValue / post.likes : 0,
          cpc: post.comments > 0 ? costValue / post.comments : 0,
          cpe: totalEngagement > 0 ? costValue / totalEngagement : 0,
        };
      }
      return post;
    });

    setPosts(updatedPosts);
    setEditingCostId(null);
    setEditCostValue('');
    toast.success('Cost updated successfully');
    }else{
    toast.error('Cost not updated');
    }
    
    }catch(error){
      console.error(`handleSaveCost ERROR ::: ${error}`)
    }
    
  };

  const handleCancelEditCost = () => {
    setEditingCostId(null);
    setEditCostValue('');
  };

  const fullTitle = selectedPost?.title ?? "";
const truncatedTitle =
  fullTitle.length > 100 ? fullTitle.substring(0, 100) + "..." : fullTitle;

useEffect(() => {
  const totalPages = Math.max(1, Math.ceil(totalCount / limit));

  if (page > totalPages) {
    setPage(1);
  }
}, [totalCount]);
const PostRowSkeleton = ({
  showPlatformColumn,
  showSharesColumn,
  showRepostsColumn,
}: {
  showPlatformColumn: boolean;
  showSharesColumn: boolean;
  showRepostsColumn: boolean;
}) => {
  return (
    <TableRow>
      {/* Post */}
      <TableCell>
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-[180px]" />
            <Skeleton className="h-3 w-[120px]" />
          </div>
        </div>
      </TableCell>

      {/* Platform */}
      {showPlatformColumn && (
        <TableCell>
          <Skeleton className="h-6 w-[80px] rounded-full" />
        </TableCell>
      )}

      {/* Type */}
      <TableCell>
        <Skeleton className="h-6 w-[50px] rounded-md" />
      </TableCell>

      {/* Published */}
      <TableCell>
        <Skeleton className="h-4 w-[80px]" />
      </TableCell>

      {/* Views */}
      <TableCell className="text-right">
        <Skeleton className="h-4 w-[70px] ml-auto" />
      </TableCell>

      {/* Likes */}
      <TableCell className="text-right">
        <Skeleton className="h-4 w-[60px] ml-auto" />
      </TableCell>

      {/* Comments */}
      <TableCell className="text-right">
        <Skeleton className="h-4 w-[50px] ml-auto" />
      </TableCell>

      {/* Shares */}
      {showSharesColumn && (
        <TableCell className="text-right">
          <Skeleton className="h-4 w-[50px] ml-auto" />
        </TableCell>
      )}

      {/* Reposts */}
      {showRepostsColumn && (
        <TableCell className="text-right">
          <Skeleton className="h-4 w-[50px] ml-auto" />
        </TableCell>
      )}

      {/* ER */}
      <TableCell className="text-center">
        <Skeleton className="h-4 w-[50px] mx-auto" />
      </TableCell>

      {/* Cost + CPV/CPL/CPC/CPE -- hidden on shared links */}
      {!shared && (<>
      {/* Cost */}
      <TableCell className="text-center">
        <Skeleton className="h-8 w-[80px] rounded-md mx-auto" />
      </TableCell>

      {/* CPV */}
      <TableCell className="text-right">
        <Skeleton className="h-4 w-[50px] ml-auto" />
      </TableCell>

      {/* CPL */}
      <TableCell className="text-right">
        <Skeleton className="h-4 w-[60px] ml-auto" />
      </TableCell>

      {/* CPC */}
      <TableCell className="text-right">
        <Skeleton className="h-4 w-[50px] ml-auto" />
      </TableCell>

      {/* CPE */}
      <TableCell className="text-right">
        <Skeleton className="h-4 w-[50px] ml-auto" />
      </TableCell>
      </>)}

      {/* Actions */}
      <TableCell>
        <Skeleton className="h-8 w-8 rounded-md ml-auto" />
      </TableCell>
    </TableRow>
  );
};

function renderPagination({
  page,
  totalCount,
  limit,
  loading,
  setPage,
}: {
  page: number;
  totalCount: number;
  limit: number;
  loading: boolean;
  setPage: (p: number) => void;
}) {
  const totalPages = Math.ceil(totalCount / limit);
  if (totalPages <= 1) return null;

  const pages: (number | '...')[] = [];
  const delta = 1;

  pages.push(1);

  if (page - delta > 2) pages.push('...');

  for (
    let i = Math.max(2, page - delta);
    i <= Math.min(totalPages - 1, page + delta);
    i++
  ) {
    pages.push(i);
  }

  if (page + delta < totalPages - 1) pages.push('...');

  if (totalPages > 1) pages.push(totalPages);

  return pages.map((p, i) =>
    p === '...' ? (
      <span key={`ellipsis-${i}`} className="px-2 text-slate-400">
        …
      </span>
    ) : (
      <Button
        key={p}
        size="sm"
        variant={p === page ? 'default' : 'outline'}
        disabled={loading}
        className="min-w-[36px]"
        onClick={() => setPage(p)}
      >
        {p}
      </Button>
    )
  );
}
  return (
    // PDF: no HTML borders -- the table is cut across pages, so the PDF draws a
    // closed box around each page's part instead (see data-pdf-frame)
    <Card className={pdfMode ? 'border-0 shadow-none' : undefined}>
      {/* mobile PDF: a list of post cards -- no outer box or side padding, the cards carry the borders */}
      <CardHeader className={pdfVariant === 'mobile' ? 'px-0' : undefined}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle>Post-Level Performance</CardTitle>
            <CardDescription>Detailed metrics for each post in the campaign</CardDescription>
          </div>
          {/* Bright Data often returns 0 plays, leaving Views flat at 0 here and
              in the Views-over-time chart. Admins get the posts x dates grid to
              fill the real numbers in for the whole campaign at once. */}
          {canEditViews && !pdfMode && (
            <button
              className="text-xs btn btn-primary btn-sm flex-shrink-0"
              onClick={() => setViewsGridOpen(true)}
            >
              Update Views
            </button>
          )}
        </div>
      </CardHeader>
      <CardContent className={pdfVariant === 'mobile' ? 'px-0' : undefined}>
        {/* Posts Table */}
          {/* Mobile PDF: the wide table would need sideways scrolling, so each post is a card */}
          {pdfVariant === 'mobile' ? (
            <div className="space-y-3">
              {displayPosts.length === 0 && <NoData message="No posts available for selected range" />}
              {displayPosts.map((post: any, i: number) => {
                const isFb = (post.platform || platform)?.toLowerCase() === 'facebook';
                const isIg = (post.platform || platform)?.toLowerCase() === 'instagram';
                const stats: [string, React.ReactNode][] = [
                  ['Views', <NumberWithTooltip value={post.views} tooltipComponent={Tooltip} />],
                  ['Likes', <NumberWithTooltip value={post.likes} tooltipComponent={Tooltip} />],
                  ['Comments', <NumberWithTooltip value={post.comments} tooltipComponent={Tooltip} />],
                ];
                if (showSharesColumn) stats.push(['Shares', isFb && post.shares > 0 ? post.shares : '-']);
                if (showRepostsColumn) stats.push(['Reposts', isIg && post.reposts > 0 ? <NumberWithTooltip value={post.reposts} tooltipComponent={Tooltip} /> : '-']);
                stats.push(['ER', <span className="text-green-600">{post.er}%</span>]);
                return (
                  <div key={post.url_id ?? i} data-pdf-row className="rounded-lg border p-3">
                    <div className="flex gap-3">
                      <img
                        src={post.image || DummyCampaignImage.default_campaign_image}
                        alt=""
                        className="w-10 h-10 rounded-lg object-cover shrink-0"
                      />
                      <div className="min-w-0">
                        <a href={post.postUrl || '#'} target="_blank" rel="noopener noreferrer" className="text-blue-600 block whitespace-normal break-words">
                          {isUnavailable(post) ? 'Post unavailable' : pdfText(post.title, 90) || '-'}
                        </a>
                        {isUnavailable(post) ? (
                          <div className="text-xs text-red-600">{unavailableReason(post)}</div>
                        ) : (
                          <div className="text-sm text-slate-500">@{post.username}</div>
                        )}
                        {Number(post.duration) > 0 && (
                        <span style={{ color: '#787878', fontSize: '12px' }}>Duration: {methods.formatDuration(Number(post.duration))}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-slate-600">
                      {showPlatformColumn && <span className="rounded bg-slate-100 px-2 py-0.5 capitalize">{post.platform}</span>}
                      {post.type && <span className="rounded bg-amber-100 px-2 py-0.5">{post.type}</span>}
                      <span>{String(post.publishedDate ?? '').slice(0, 10)}</span>
                    </div>
                    {/* all stats on one line keeps each card short, so more posts fit a page */}
                    <div className="grid gap-1.5 mt-2" style={{ gridTemplateColumns: `repeat(${stats.length}, minmax(0, 1fr))` }}>
                      {stats.map(([label, value]) => (
                        <div key={label} className="rounded-md bg-slate-50 px-1.5 py-1 min-w-0">
                          <div className="text-[11px] text-slate-500">{label}</div>
                          <div className="text-sm text-slate-900">{value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
          <ScrollArea className={`w-full h-auto rounded-md ${pdfMode ? '' : 'border'} flex d-inherit`}>
            <div className="min-w-max">
              {/* PDF: a page can't scroll sideways, so the table is compacted to fit the page width */}
              <Table className={pdfMode ? 'text-[12px] [&_td]:px-1.5 [&_th]:px-1.5' : undefined}>
            <TableHeader data-pdf-repeat>
              <TableRow>
                <TableHead>Post</TableHead>
                {showPlatformColumn && <TableHead>Platform</TableHead>}
                <TableHead>Type</TableHead>
                <TableHead>Published</TableHead>
                <TableHead className="text-right">
                  <button
                    onClick={() => handleSort('views')}
                    className="hover:text-slate-900 inline-flex items-center"
                  >
                    Views
                    {renderSortIcon('views')}
                  </button>
                </TableHead>
                <TableHead className="text-right">
                  <button
                    onClick={() => handleSort('likes')}
                    className="hover:text-slate-900 inline-flex items-center"
                  >
                    Likes
                    {renderSortIcon('likes')}
                  </button>
                </TableHead>
                <TableHead className="text-right">
                  <button
                    onClick={() => handleSort('comments')}
                    className="hover:text-slate-900 inline-flex items-center"
                  >
                    Comments
                    {renderSortIcon('comments')}
                  </button>
                </TableHead>
                {showSharesColumn && <TableHead className="text-right">Shares</TableHead>}
                {showRepostsColumn && <TableHead className="text-right">Reposts</TableHead>}
                <TableHead className="text-right">
                  <button
                    onClick={() => handleSort('er')}
                    className="hover:text-slate-900 inline-flex items-center"
                  >
                    ER
                    {renderSortIcon('er')}
                  </button>
                </TableHead>
                {!shared && (
                <TableHead className="text-right">
                  <button
                    onClick={() => handleSort('cost')}
                    className="hover:text-slate-900 inline-flex items-center"
                  >
                    Cost
                    {renderSortIcon('cost')}
                  </button>
                </TableHead>)}
                {/* CPV/CPL/CPC/CPE are cost-derived -- hidden on shared links along with Cost */}
                {!shared && (<>
                <TableHead className="text-right">
                  <button
                    onClick={() => handleSort('cpv')}
                    className="hover:text-slate-900 inline-flex items-center"
                  >
                    CPV
                    {renderSortIcon('cpv')}
                  </button>
                </TableHead>
                <TableHead className="text-right">
                  <button
                    onClick={() => handleSort('cpl')}
                    className="hover:text-slate-900 inline-flex items-center"
                  >
                    CPL
                    {renderSortIcon('cpl')}
                  </button>
                </TableHead>
                <TableHead className="text-right">
                  <button
                    onClick={() => handleSort('cpc')}
                    className="hover:text-slate-900 inline-flex items-center"
                  >
                    CPC
                    {renderSortIcon('cpc')}
                  </button>
                </TableHead>
                <TableHead className="text-right">
                  <button
                    onClick={() => handleSort('cpe')}
                    className="hover:text-slate-900 inline-flex items-center"
                  >
                    CPE
                    {renderSortIcon('cpe')}
                  </button>
                </TableHead>
                </>)}
                {!pdfMode && <TableHead className="w-[50px]"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <>
                  {Array.from({ length: limit }).map((_, i) => (
                    <PostRowSkeleton
                      key={i}
                      showPlatformColumn={showPlatformColumn}
                      showSharesColumn={showSharesColumn}
                      showRepostsColumn={showRepostsColumn}
                    />
                  ))}
                </>
              )}

              {/* NO DATA */}
              {!loading && displayPosts.length === 0 && (
                <TableRow>
                  <TableCell
                    // colSpan={
                    //   showPlatformColumn
                    //     ? showSharesColumn
                    //       ? 14
                    //       : 13
                    //     : showSharesColumn
                    //     ? 13
                    //     : 12
                    // }
                    // Shared links drop the 5 cost columns (Cost, CPV, CPL, CPC, CPE)
                    colSpan={
                      (showPlatformColumn
                        ? showSharesColumn
                          ? 14
                          : 13
                        : showSharesColumn
                        ? 13
                        : 12) - (shared ? 5 : 0) + (showRepostsColumn ? 1 : 0) - (pdfMode ? 1 : 0)
                    }
                    className="text-center py-6"
                  >
                    <NoData message="No posts available for selected range" />
                  </TableCell>
                </TableRow>
              )}
              
              {!loading && displayPosts.map((post:any) => {
                return <TableRow key={post.url_id} data-pdf-row>
                  <TableCell>
                    <div className="flex flex-col">
                      <div className='d-flex'>
                        <a
                          href={post.postUrl || '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline max-w-[200px] truncate mr-2"
                        >
                         <img
                          src={post.image || DummyCampaignImage.default_campaign_image}
                          alt={post.title}
                          className="w-10 h-10 rounded-lg object-cover shrink-0"
                        />
                        </a>
                        <div className='flex flex-col'>
                          <a
                            href={post.postUrl || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`text-blue-600 hover:underline ${pdfMode ? 'w-[230px] block whitespace-normal break-words' : 'max-w-[200px] truncate'}`}
                          >

                            <DarkTooltip title={isUnavailable(post) ? (post.postUrl || '') : post.title} placement="top-start" arrow>
                            {/* {post.title} */}
                            <span>{isUnavailable(post) ? 'Post unavailable' : pdfMode ? pdfText(post.title, 90) : post.title}</span>
                            </DarkTooltip>
                          </a>
                          {isUnavailable(post) ? (
                            <span className="text-xs text-red-600 max-w-[230px] whitespace-normal">{unavailableReason(post)}</span>
                          ) : (
                          <Link
                            to={
                              post.platform === 'facebook'
                                ? `${BASE_URL_UI}/brand/profile-insight-fb?profile_id=${post.profile_id || post.url_id}`
                                : post.platform === 'youtube'
                                ? `${BASE_URL_UI}/brand/profile-insight-yt?sm_id=${post.profile_id}&influencerId=${post.influencer_id}`
                                : `${BASE_URL_UI}/brand/profile-insight?sm_id=${post.profile_id}&influencerId=${post.influencer_id}`
                            }
                            target="_blank"
                          >
                            <span className="text-slate-600 text-sm">@{post.username}</span>
                          </Link>
                          )}
                        {Number(post.duration) > 0 && (
                        <span style={{ color: '#787878', fontSize: '12px' }}>Duration: {methods.formatDuration(Number(post.duration))}</span>
                        )}
                        {/* Instagram post the scraper couldn't read (warning stored): admins can give the creator's username to retry */}
                        {canAddUsernames && !pdfMode && isUnavailable(post) && !!post?.warning_code && (post.platform || platform)?.toLowerCase() === 'instagram' && (
                          <span className="inline-flex items-center gap-1 text-xs">
                            {savedUsernames[Number(post.url_id)] && (
                              <span className="text-slate-500">@{savedUsernames[Number(post.url_id)]} ·</span>
                            )}
                            <button
                              type="button"
                              className="text-blue-600 hover:underline text-left disabled:text-slate-400 disabled:no-underline disabled:cursor-not-allowed"
                              disabled={checkingUsernames}
                              onClick={() => { setUsernamePost(post); setUsernameValue(savedUsernames[Number(post.url_id)] || ''); setUsernameError(undefined); }}
                            >
                              {savedUsernames[Number(post.url_id)] ? 'Change username' : 'Add username'}
                            </button>
                            <span
                              className="inline-flex text-slate-400 cursor-help"
                              title="We couldn't fetch this post from Instagram. Add the creator's Instagram username and we'll look for the post on their profile instead."
                              aria-label="Why add a username?"
                            >
                              <Info size={13} />
                            </span>
                          </span>
                        )}
                        </div> 
                      </div>
                    </div>
                  </TableCell>
                  {showPlatformColumn && (
                    <TableCell>
                      <Badge variant="secondary" className={getPlatformColor(post.platform)}>
                        {post.platform}
                      </Badge>
                    </TableCell>
                  )}
                  <TableCell>
                    <span style={getTierBadgeStyle(post.type, CONTENT_TYPE_COLORS)}>
                    {post?.type
                        ? post.type.charAt(0).toUpperCase() + post.type.slice(1)
                        : ''}

                    </span>
                  </TableCell>
                  <TableCell className="text-slate-600">{post.publishedDate}</TableCell>
                  <TableCell className="text-right">

                  <NumberWithTooltip value={post.views} tooltipComponent={Tooltip} />
                    
                  </TableCell>
                  <TableCell className="text-right">
                     <NumberWithTooltip value={post.likes} tooltipComponent={Tooltip} />
                    </TableCell>
                  <TableCell className="text-right">
                    <NumberWithTooltip value={post.comments} tooltipComponent={Tooltip} />
                  </TableCell>
                  {showSharesColumn && (
                    <TableCell className="text-right">
                      {/* {(platform === 'all' && post.platform === 'Facebook') || platform.toLowerCase() === 'facebook' */}
                      {/* API returns lowercase 'facebook', so the old 'Facebook' match never hit */}
                      {(platform === 'all' && post.platform?.toLowerCase() === 'facebook') || platform.toLowerCase() === 'facebook'
                        ? (post.shares > 0 ? post.shares : '-')
                        : '-'}
                    </TableCell>
                  )}
                  {showRepostsColumn && (
                    <TableCell className="text-right">
                      {post.platform?.toLowerCase() === 'instagram' || platform.toLowerCase() === 'instagram'
                        ? (post.reposts > 0 ? <NumberWithTooltip value={post.reposts} tooltipComponent={Tooltip} /> : '-')
                        : '-'}
                    </TableCell>
                  )}
                  <TableCell className="text-center text-green-600">{post.er}%</TableCell>
                  {!shared && (<TableCell className={`text-right ${pdfMode ? '' : 'min-w-150'}`}>
                    {pdfMode ? (
                      post.cost ? `₹${post.cost.toFixed(0)}` : '-'
                    ) : editingCostId === post.url_id ? (
                      <div className="flex items-center justify-content-end gap-2">
                        <Input
                          type="number"
                          value={editCostValue}
                          onChange={(e) => setEditCostValue(e.target.value)}
                          className="h-8 w-16 text-right px-1"
                          placeholder="0"
                          step={0.1}
                          min="0"
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-4 w-4 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                          onClick={() => handleSaveCost(post.url_id)}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-4 w-4 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={handleCancelEditCost}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : post.cost ? (
                      <div className="flex w-100 items-center justify-content-end gap-1">
                        <span>₹{post?.cost?.toFixed(0)}</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          onClick={() => handleEditCost(post.url_id, post.cost)}
                        >
                          <Edit2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex w-100 items-center justify-content-end">
                        <button
                          className="text-xs btn btn-primary btn-sm leading-none"
                          onClick={() => handleEditCost(post.url_id)}
                        >
                          Add Cost
                        </button>
                      </div>
                    )}
                  </TableCell>)}
                  {/* CPV/CPL/CPC/CPE are cost-derived -- hidden on shared links along with Cost */}
                  {!shared && (<>
                  <TableCell className="text-right">
                    {post?.cost ? `₹${post?.cpv?.toFixed(2)}` : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    {post?.cost ? `₹${post?.cpl?.toFixed(2)}` : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    {post?.cost ? `₹${post?.cpc?.toFixed(2)}` : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    {post?.cost ? `₹${post?.cpe?.toFixed(2)}` : '-'}
                  </TableCell>
                  </>)}
                  {!pdfMode && (
                  <TableCell>
                    {!shared && (
                      <Button
                        variant="ghost"
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => {
                        const id = post.url_id ?? post.id;
                        setDeletePostId(id);
                        setSelectedPost(post);   // <-- store full post
                      }}
                      >
                      <Trash2 className="h-4 w-4" />
                    </Button>)}
                  </TableCell>)}
                </TableRow>
              })}
              
            </TableBody>
          </Table>
              </div>  
              <ScrollBar orientation="horizontal" />


          </ScrollArea>
          )}
          {!pdfMode && (
          <div className="flex items-center justify-between mt-3 px-2 mb-0 ms-auto">    
  {/* Left: Showing info */}
  {/* <span className="text-sm text-slate-600">
    Showing {(page - 1) * limit + 1}–
    {Math.min(page * limit, totalCount)} of {totalCount}
  </span> */}

  {/* Right: Pagination */}
  <div className="flex ms-auto items-center gap-1">
    {/* Prev */}
    

    {/* Page Numbers */}
    {/* {renderPagination({
      page,
      totalCount,
      limit,
      loading,
      setPage,
    })} */}

    <div className="flex items-center gap-1">
      <span className="text-sm text-slate-600">Rows:</span>
      <select
        className="border rounded-md px-2 py-1 text-sm bg-white disabled:opacity-50"
        value={limit}
        disabled={loading}
        onChange={(e) => {
          setLimit(Number(e.target.value));
          setPage(1);
        }}
      >
        {[10, 25, 50, 100].map((size) => (
          <option key={size} value={size}>
            {size}
          </option>
        ))}
      </select>
    </div>

    <span className="text-sm text-slate-600 me-2">
      Showing {(page - 1) * limit + 1}–
      {Math.min(page * limit, totalCount)} of {totalCount}
    </span>
    <Button
      variant="ghost"
      size="sm"
      disabled={page === 1 || loading}
      onClick={() => setPage(p => p - 1)}
    >

      <MaterialSymbol fontweight='200' iconName='chevron_left' />
    </Button>
    {/* Next */}
    <Button
      variant="ghost"
      size="sm"
      disabled={page >= Math.ceil(totalCount / limit) || loading}
      onClick={() => setPage(p => p + 1)}
    >
     <MaterialSymbol fontweight='200' iconName='chevron_right' />
    </Button>
  </div>
</div>
          )}
      </CardContent>

      {/* Add Post Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setNewUsernameError(undefined); }} >
        <DialogContent className="sm:max-w-[500px] mx-auto bg-white">
          <DialogHeader>
            <DialogTitle>Add New Post</DialogTitle>
            <DialogDescription>
              Enter the post link, and optional username and cost, to add it to the campaign.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="postLink">Post Link</Label>
              <Input
                id="postLink"
                placeholder="Enter post URL"
                value={postLink}
                onChange={(e) => setPostLink(e.target.value)}
                // Convert pasted instagram.com/<username>/reel/<code> links to the canonical form straight away.
                onPaste={(e) => {
                  const pasted = e.clipboardData.getData('text').trim();
                  const normalized = formValidation.normalizeInstagramPostUrl(pasted);
                  if (normalized !== pasted) {
                    e.preventDefault();
                    setPostLink(normalized);
                  }
                }}
                onBlur={() => setPostLink((v) => formValidation.normalizeInstagramPostUrl(v))}
              />
              <span className="error">{errorPostLink?.message}</span>
            </div>
            {canAddUsernames && rowTakesUsername(postLink) && (
              <div className="grid gap-2">
                <Label htmlFor="newUsername">Creator&apos;s Instagram username (Optional)</Label>
                <Input
                  id="newUsername"
                  placeholder="e.g. creator_name"
                  maxLength={100}
                  autoCapitalize="none"
                  spellCheck={false}
                  value={newUsername}
                  onChange={(e) => { setNewUsername(e.target.value); setNewUsernameError(undefined); }}
                />
                {newUsernameError
                  ? <span className="error">{newUsernameError}</span>
                  : <span className="text-xs text-slate-500">If Instagram doesn&apos;t show us this post, we use the username to find it on the creator&apos;s profile.</span>}
              </div>
            )}
            <div className="grid gap-2">
              <Label htmlFor="cost">Cost (Optional)</Label>
              <Input
                id="cost"
                type="number"
                placeholder="0.00"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
                step="0.01"
                min="0"
              />
            </div>
          </div>
          {/* Bulk add via Excel - adding posts one at a time was too slow for big campaigns */}
          <BulkAddPosts
            campaignId={linkedCampaignId}
            withUsername={canAddUsernames}
            onDone={() => {
              setDialogOpen(false);
              onDeleteSuccess?.();
            }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <button onClick={handleAddPost} className="btn btn-primary flex btn-sm align-items-center">
              <Plus className="h-4 w-4 mr-1" />
              Add Post
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ManualViewsGrid
        open={viewsGridOpen}
        onClose={() => setViewsGridOpen(false)}
        campaignId={linkedCampaignId}
        posts={posts || []}
        platform={platform}
        onSaved={() => onDeleteSuccess?.()}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={usernamePost !== null} onOpenChange={(open) => { if (!open) { setUsernamePost(null); setUsernameValue(''); setUsernameError(undefined); } }}>
        <DialogContent className="sm:max-w-[500px] mx-auto bg-white">
          <DialogHeader>
            <DialogTitle>{savedUsernames[Number(usernamePost?.url_id)] ? 'Change Username' : 'Add Username'}</DialogTitle>
            <DialogDescription>
              We couldn&apos;t fetch this post from Instagram. Enter the Instagram username of the creator who posted it and we&apos;ll look for the post on their profile instead.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="usernamePostUrl">Post URL</Label>
              <Input id="usernamePostUrl" value={usernamePost?.postUrl || ''} disabled readOnly />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="postUsername">Creator&apos;s Instagram username</Label>
              <Input
                id="postUsername"
                placeholder="e.g. creator_name"
                maxLength={100}
                autoCapitalize="none"
                spellCheck={false}
                value={usernameValue}
                onChange={(e) => { setUsernameValue(e.target.value); setUsernameError(undefined); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSaveUsername(); } }}
              />
              {!!usernameError && <span className="error">{usernameError}</span>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setUsernamePost(null); setUsernameValue(''); setUsernameError(undefined); }} disabled={savingUsername}>
              Cancel
            </Button>
            <Button onClick={handleSaveUsername} disabled={!usernameValue.trim() || savingUsername}>
              {savingUsername ? 'Saving…' : 'Save Username'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deletePostId !== null} onOpenChange={() => {
  setDeletePostId(null);
  setSelectedPost(null);
}}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className='pb-2'>Are you sure you want to delete this post?</AlertDialogTitle>

            <AlertDialogDescription className="pb-3 space-y-3">

              {selectedPost && (
                <div className="flex items-start gap-3 p-3 bg-slate-100 rounded-md">
                  <img
                    src={selectedPost.image || DummyCampaignImage.default_campaign_image}
                    alt={selectedPost.title}
                    className="w-16 h-16 rounded-md object-cover"
                  />
                  <div>
                    <p className="fs-14 text-slate-900 text-left pb-2">
                      <DarkTooltip title={fullTitle} placement="top-start" arrow>
                      {truncatedTitle}
                      </DarkTooltip>
                      
                    </p>
                    <p className="text-slate-500 text-sm text-left">@{selectedPost.username}</p>
                  </div>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>

            <AlertDialogAction
              onClick={() => deletePostId && handleDeletePost(deletePostId)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* <AlertDialog open={deletePostId !== null} onOpenChange={() => setDeletePostId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription  className='pb-3'>
              <>{console.log(deletePostId, "deletePostId")}</>
              This action cannot be undone. This will permanently delete the post from the campaign.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            
            <AlertDialogAction
              onClick={() => deletePostId && handleDeletePost(deletePostId)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog> */}
    </Card>
  );
}
